/**
 * User Stats Page
 *
 * Read-only view of user statistics:
 * - Account information
 * - AI usage metrics
 * - Disk usage breakdown
 * - Session history
 * - App permissions (read-only)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getUserDetail,
  getUserFileStats,
  getUserCosts,
  type UserDetail,
  type UserFileStats,
  type UserCosts,
} from '@/services/adminApi';

export default function UserStatsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('admin');

  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [fileStats, setFileStats] = useState<UserFileStats | null>(null);
  const [costs, setCosts] = useState<UserCosts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userId) {
      loadData(parseInt(userId));
    }
  }, [userId]);

  const loadData = async (id: number) => {
    setIsLoading(true);
    setError('');
    try {
      const [userDetail, stats, userCosts] = await Promise.all([
        getUserDetail(id),
        getUserFileStats(id),
        getUserCosts(id).catch(() => null),
      ]);
      setDetail(userDetail);
      setFileStats(stats);
      setCosts(userCosts);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date helper
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format bytes to human readable
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Calculate account age
  const getAccountAge = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
    return `${Math.floor(diffDays / 365)} years`;
  };

  // Parse user agent for display
  const parseUserAgent = (ua: string | null) => {
    if (!ua) return 'Unknown device';
    if (ua.includes('Chrome')) return `Chrome on ${ua.includes('Windows') ? 'Windows' : ua.includes('Mac') ? 'Mac' : 'Linux'}`;
    if (ua.includes('Firefox')) return `Firefox on ${ua.includes('Windows') ? 'Windows' : ua.includes('Mac') ? 'Mac' : 'Linux'}`;
    if (ua.includes('Safari')) return `Safari on ${ua.includes('iPhone') ? 'iOS' : 'Mac'}`;
    return ua.substring(0, 50);
  };

  // Calculate storage percentage
  const getStoragePercentage = (size: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((size / total) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || t('common.userNotFound')}
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          &larr; {t('navigation.backToUsers')}
        </button>
      </div>
    );
  }

  const { user, sessions, usage } = detail;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin')}
          className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
        >
          <span>&larr;</span> {t('navigation.backToUsers')}
        </button>
      </div>

      {/* User Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
            {user.full_name?.charAt(0) || user.username.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900">{user.full_name || user.username}</h1>
            <div className="flex items-center gap-2 mt-1 text-gray-600">
              <span>@{user.username}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {user.role}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                user.status === 'active' ? 'bg-green-100 text-green-700' :
                user.status === 'blocked' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {user.status}
              </span>
            </div>
            <div className="text-gray-500 mt-1">{user.email}</div>
          </div>
        </div>
      </div>

      {/* Cost Overview */}
      {costs && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Cost Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600">AI Tasks (30d)</p>
              <p className="text-2xl font-bold text-red-900">${costs.ai_cost_30d.toFixed(2)}</p>
              <p className="text-xs text-red-500">${costs.total_ai_cost.toFixed(2)} all time</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-600">File Processing</p>
              <p className="text-2xl font-bold text-orange-900">${costs.total_processing_cost.toFixed(2)}</p>
              <p className="text-xs text-orange-500">{costs.files_processed} files</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">Avg / Iteration</p>
              <p className="text-2xl font-bold text-blue-900">${costs.avg_cost_per_iteration.toFixed(4)}</p>
              <p className="text-xs text-blue-500">{costs.iterations_completed} iterations</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Spend</p>
              <p className={`text-2xl font-bold ${
                (costs.total_ai_cost + costs.total_processing_cost) > 10
                  ? 'text-red-600'
                  : (costs.total_ai_cost + costs.total_processing_cost) > 3
                    ? 'text-amber-600'
                    : 'text-green-600'
              }`}>
                ${(costs.total_ai_cost + costs.total_processing_cost).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">AI + Processing</p>
            </div>
          </div>
        </div>
      )}

      {/* Account Info Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{t('userDetail.created')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{t('userDetail.lastLogin')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {user.last_login ? new Date(user.last_login).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : t('users.never')}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{t('userStats.accountAge')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {getAccountAge(user.created_at)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{t('userDetail.status')}</p>
          <p className={`text-lg font-semibold capitalize ${
            user.status === 'active' ? 'text-green-600' :
            user.status === 'blocked' ? 'text-red-600' :
            'text-gray-600'
          }`}>
            {user.status}
          </p>
        </div>
      </div>

      {/* Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userStats.activity')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="p-4 bg-indigo-50 rounded-lg">
            <p className="text-sm text-indigo-600">{t('userStats.tasks')}</p>
            <p className="text-2xl font-bold text-indigo-900">{usage.tasks_total}</p>
            <p className="text-xs text-indigo-500">{usage.tasks_completed} {t('userStats.completed')}</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-600">{t('userStats.iterations')}</p>
            <p className="text-2xl font-bold text-purple-900">{usage.iterations_total}</p>
            <p className="text-xs text-purple-500">{usage.iterations_completed} {t('userStats.done')}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600">{t('userStats.contacts')}</p>
            <p className="text-2xl font-bold text-green-900">{usage.contacts_total}</p>
            <p className="text-xs text-green-500">{usage.companies_total} {t('userStats.companies')}</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600">{t('userStats.calendar')}</p>
            <p className="text-2xl font-bold text-orange-900">{usage.events_total}</p>
            <p className="text-xs text-orange-500">{usage.events_upcoming} {t('userStats.upcoming')}</p>
          </div>
        </div>

        {/* Task Breakdown */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-2">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            {t('userStats.completed')}: {usage.tasks_completed}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            {t('userStats.inProgress')}: {usage.tasks_in_progress}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
            {t('userStats.pending')}: {usage.tasks_pending}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-gray-400"></span>
            {t('userStats.archived')}: {usage.tasks_archived}
          </span>
        </div>
        <div className="text-sm text-gray-500 mb-2">
          {t('userStats.filesLabel')}: {usage.files_total} {t('userStats.files')}, {usage.folders_total} {t('userStats.folders')} | {t('userStats.knowledge')}: {usage.kb_total} KB, {usage.cogito_entities} {t('userStats.entities')}, {usage.cogito_insights} {t('userStats.insights')}
        </div>

        {usage.last_activity_at && (
          <p className="text-xs text-gray-400">
            {t('userStats.lastActivity')}: {formatDate(usage.last_activity_at)}
          </p>
        )}
      </div>

      {/* Disk Usage */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userStats.diskUsage')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">{t('userStats.totalFiles')}</p>
            <p className="text-2xl font-bold text-gray-900">{fileStats?.total_files || 0}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">{t('userStats.storageUsed')}</p>
            <p className="text-2xl font-bold text-gray-900">{formatBytes(fileStats?.total_size_bytes || 0)}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">{t('userStats.documents')}</p>
            <p className="text-2xl font-bold text-gray-900">{fileStats?.document_count || 0}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">{t('userStats.mediaFiles')}</p>
            <p className="text-2xl font-bold text-gray-900">{fileStats?.image_count || 0}</p>
          </div>
        </div>

        {/* Storage Breakdown */}
        {fileStats && fileStats.total_size_bytes > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">{t('userStats.storageBreakdown')}</h3>
            {/* Documents */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{t('userStats.documents')}</span>
                  <span className="text-gray-500">
                    {formatBytes(fileStats.document_size)} ({getStoragePercentage(fileStats.document_size, fileStats.total_size_bytes)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${getStoragePercentage(fileStats.document_size, fileStats.total_size_bytes)}%` }}
                  />
                </div>
              </div>
            </div>
            {/* Images */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{t('userStats.images')}</span>
                  <span className="text-gray-500">
                    {formatBytes(fileStats.image_size)} ({getStoragePercentage(fileStats.image_size, fileStats.total_size_bytes)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${getStoragePercentage(fileStats.image_size, fileStats.total_size_bytes)}%` }}
                  />
                </div>
              </div>
            </div>
            {/* Other */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{t('userStats.other')}</span>
                  <span className="text-gray-500">
                    {formatBytes(fileStats.other_size)} ({getStoragePercentage(fileStats.other_size, fileStats.total_size_bytes)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-500 rounded-full"
                    style={{ width: `${getStoragePercentage(fileStats.other_size, fileStats.total_size_bytes)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Session History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userStats.sessionHistoryLast10')}</h2>
        {sessions.length === 0 ? (
          <p className="text-gray-500">{t('userDetail.noSessionHistory')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">{t('userDetail.status')}</th>
                  <th className="pb-2">{t('userDetail.device')}</th>
                  <th className="pb-2">{t('userDetail.ipAddress')}</th>
                  <th className="pb-2">{t('userDetail.lastActive')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.slice(0, 10).map((session) => (
                  <tr key={session.id} className="border-b last:border-0">
                    <td className="py-2">
                      <span className={`inline-flex items-center gap-1 ${
                        session.is_active ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          session.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}></span>
                        {session.is_active ? t('users.active') : 'Expired'}
                      </span>
                    </td>
                    <td className="py-2 text-gray-700">{parseUserAgent(session.user_agent)}</td>
                    <td className="py-2 text-gray-600 font-mono text-xs">{session.ip_address || 'N/A'}</td>
                    <td className="py-2 text-gray-500">{formatDate(session.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* App Permissions (Read-Only) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userStats.appPermissionsReadOnly')}</h2>
        <div className="flex flex-wrap gap-3">
          {[
            { key: 'calendar' as const, label: t('userDetail.calendar') },
            { key: 'email' as const, label: t('userDetail.email') },
            { key: 'knowledge_base' as const, label: t('userDetail.kb') },
            { key: 'cogito' as const, label: t('userDetail.cogito') },
            { key: 'tasks' as const, label: t('userDetail.tasks') },
            { key: 'disk' as const, label: t('userDetail.disk') },
            { key: 'people' as const, label: t('userDetail.people') },
            { key: 'chat' as const, label: t('userDetail.chat') },
            { key: 'contractors' as const, label: t('userDetail.contractors') },
          ].map(({ key, label }) => (
            <span
              key={key}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm ${
                user.app_permissions[key]
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {user.app_permissions[key] ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              {label}
            </span>
          ))}
          <span
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm ${
              user.voice_enabled
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {user.voice_enabled ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            {t('userDetail.voiceUI')}
          </span>
        </div>
      </div>
    </div>
  );
}
